import { formatDistanceToNow } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Mail, MailOpen, Paperclip } from "lucide-react";
import type { Database } from "@/integrations/supabase/types";

type Message = Database["public"]["Tables"]["messages"]["Row"];

interface MessageInboxProps {
  messages: Message[];
  onMessageSelect: (message: Message) => void;
  selectedMessage: Message | null;
  showSent?: boolean;
}

export const MessageInbox = ({ 
  messages, 
  onMessageSelect, 
  selectedMessage,
  showSent = false 
}: MessageInboxProps) => {
  const getMessageTypeColor = (type: string) => {
    switch (type) {
      case 'urgent':
        return 'destructive';
      case 'important':
        return 'default';
      default:
        return 'secondary';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-destructive';
      case 'medium':
        return 'text-warning';
      default:
        return 'text-muted-foreground';
    }
  };

  if (messages.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center text-muted-foreground">
          {showSent ? "No sent messages" : "No messages in inbox"}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      {messages.map((message) => (
        <Card
          key={message.id}
          className={cn(
            "cursor-pointer transition-colors hover:bg-muted/50",
            selectedMessage?.id === message.id && "ring-2 ring-primary"
          )}
          onClick={() => onMessageSelect(message)}
        >
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="mt-1">
                  {message.status === 'unread' && !showSent ? (
                    <Mail className="h-4 w-4 text-primary" />
                  ) : (
                    <MailOpen className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className={cn(
                      "text-sm truncate",
                      message.status === 'unread' && !showSent 
                        ? "font-semibold" 
                        : "font-normal"
                    )}>
                      {message.subject || "No subject"}
                    </p>
                    {message.priority && (
                      <Badge 
                        variant="outline" 
                        className={cn("text-xs", getPriorityColor(message.priority))}
                      >
                        {message.priority}
                      </Badge>
                    )}
                  </div>
                  
                  <p className="text-xs text-muted-foreground truncate">
                    {message.content}
                  </p>
                  
                  <div className="flex items-center gap-2 mt-2">
                    <Badge 
                      variant={getMessageTypeColor(message.message_type)}
                      className="text-xs"
                    >
                      {message.message_type.replace('_', ' ')}
                    </Badge>
                    
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(message.created_at), { 
                        addSuffix: true 
                      })}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col items-end gap-1">
                {message.status === 'unread' && !showSent && (
                  <div className="w-2 h-2 bg-primary rounded-full" />
                )}
                {/* Show paperclip icon if message has attachments */}
                <Paperclip className="h-3 w-3 text-muted-foreground opacity-50" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};